// Services
import { getSearchService, Role, RoleBundleResponse, WorkHistoryData, WorkHistoryDataWithDates } from "services";

// Utils
import { compareDesc, format } from "date-fns";
import { autoNameSearch } from "components/SetupCompany/utils";
import { capitalize, difference, differenceBy, isArray, isEmpty, isNil, mergeWith, omit, omitBy, sortBy } from "lodash";

// Constants
import theme from "assets/styles/themes";
import { AbilityIconColor, allUserRoles, functionsPillColor } from "constants/constants";

// Types
import { AxiosResponse } from "axios";
import { SVGRenderer } from "highcharts";
import { LD12Chart } from "store/slice/charts";
import { ISearchDataSimple } from "types/search";
import { Member } from "store/slice/Team/team.types";
import { Facet } from "store/slice/Project/projectData";
import { SimpleEntity, TUserRole } from "types/global";
import { TBCSectionData } from "pages/BehavioralComparison";
import { TSelectedPermissions } from "pages/HubManagementNew/components/Permissions/Permissions";
import { BriefChildren, FacetObjectNarrative, SavedFacetObjectNarrative } from "services/projects/project.types";
import {
    ICurrentSearchData,
    ISearchDataToSave,
    IPotentialMember,
} from "store/slice/currentSearch/currentSearchSlice.types";
import icons from "enums/icons";
import { Order } from "components/SortMenu/SortMenu";
import Highcharts from "highcharts";
import { TPaceAssessmentReportStatus } from "store/slice/Pace/paceSlice.types";

interface IPaceCountdown {
    months: number;
    days: number;
    hours: number;
    minutes: number;
}

export const removeFalsyParams = (obj: any) => {
    const cleanObj: any = {};
    Object.keys(obj).forEach((key: string) => {
        if (obj[key]) {
            cleanObj[key] = obj[key];
        }
    });
    return cleanObj;
};

export const createLabel = (renderer: any, { args, attr, css }: any): SVGElement => {
    const label = renderer
        .label(...args)
        .attr(attr)
        .css(css)
        .add();

    label.userOptions = { args, attr, css };

    return label;
};

export type TValidSVGElements = "label" | "g" | "button" | "path" | "symbol" | "circle";
export const createHighchartsSvg = (
    renderer: SVGRenderer,
    { args, attr, css, translate, className }: any,
    type: TValidSVGElements,
    group?: Highcharts.SVGElement
) => {
    let element = {} as Highcharts.SVGElement;

    if (type === "path") {
        element = renderer[type](args);
    } else {
        // @ts-ignore
        element = renderer[type](...args);
    }

    !isEmpty(attr) && element.attr(attr);
    !isEmpty(css) && element.css(css);
    translate?.length && element.translate(...(translate as [number, number]));
    className && element.addClass(className);

    if (group) {
        element.add(group);
    }
    return {
        svg: element,
        config: omitBy({ args, attr, css, translate, className }, isNil),
    };
};

export const getConfiguration = (name: string, x: number, y: number, rotate: boolean): any => {
    return {
        args: [name, x, y],
        attr: {
            fill: "#FFF",
            zIndex: 1,
            rotation: rotate ? 270 : 0,
        },
        css: {
            color: "#84acbc",
            fontFamily: "Lato, sans-serif",
            fontSize: "10px",
            fontWeight: 700,
        },
    };
};

type TimeStampOrString = DOMHighResTimeStamp | string;
const dateToTimeStamp = (date: string) => Math.round(new Date(date).getTime() / 1000);
export const formatDate = (timeStamp: TimeStampOrString): string => {
    const date =
        typeof timeStamp === "string" ? new Date(dateToTimeStamp(timeStamp) * 1000) : new Date(timeStamp * 1000);

    return `${date.toLocaleString("en-GB", { month: "long", year: "numeric", day: "numeric" })},
        ${date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
};
export const formatStringDate = (date: string): string => formatDate(dateToTimeStamp(date));

export const sortWorkHistory = (workHistories: WorkHistoryData[], dateField: "tenure_from" | "tenure_to") => {
    return workHistories.slice().sort((workHistoryA, workHistoryB) => {
        const dateFromA =
            workHistoryA[dateField] === null || workHistoryA[dateField] === "Present"
                ? new Date()
                : new Date(workHistoryA[dateField] as string);
        const dateFromB =
            workHistoryB[dateField] === null || workHistoryB[dateField] === "Present"
                ? new Date()
                : new Date(workHistoryB[dateField] as string);

        return compareDesc(dateFromA, dateFromB);
    });
};

export const sortWorkHistoryAddFromTo = (
    workHistories: WorkHistoryData[],
    dateField: "tenure_from" | "tenure_to"
): WorkHistoryDataWithDates[] | undefined => {
    const sortedWH = sortWorkHistory(workHistories, dateField);
    return sortedWH.map((workHistory) => {
        const from = new Date(workHistory.tenure_from).getFullYear().toString();
        const to =
            workHistory.is_current_employment && !workHistory.tenure_to
                ? "Present"
                : workHistory.tenure_to
                ? new Date(workHistory.tenure_to).getFullYear().toString()
                : "";

        return { ...workHistory, from, to };
    });
};

export const getCurrentJobWorkHistory = (workHistory: WorkHistoryData[] | undefined) =>
    workHistory?.find((wh) => wh?.is_current_employment);

export const whiteSpaceToHyphen = (label: string): string => label.toLowerCase().replace(" ", "-");

export const hexToRGB = (hex: string, alpha: number): string => {
    const r = parseInt(hex.slice(1, 3), 16),
        g = parseInt(hex.slice(3, 5), 16),
        b = parseInt(hex.slice(5, 7), 16);

    return `rgba(${r}, ${g}, ${b} ${alpha ? `, ${alpha}` : ""})`;
};

export const withOpacity = (color: string, opacity: number): string => {
    const rgba = Highcharts.color(color)?.get("rgba");
    if (typeof rgba !== "string") return color;
    return rgba.replace(/, *[\d.]+\)$/, `, ${opacity})`);
};

// TODO: type potentialMembers trying to make them compatible with the Member interface
export const createPotentialMembersStructure = (
    potentialMembers: (Member | IPotentialMember)[]
): IPotentialMember[] => {
    const extractProperties = (member: Member | IPotentialMember) => {
        return {
            key: (member as IPotentialMember)?.key || (member as Member).person_id,
            in: member.in,
            ...(member.role ? { role: member.role } : {}),
            ...(member.roleKey ? { roleKey: member.roleKey } : {}),
        };
    };

    return potentialMembers.map(extractProperties);
};

export const createDataToUpdateTalentSearch = (
    talentSearch: ISearchDataSimple,
    listsOfMembers: Member[]
): ISearchDataToSave => {
    const listsSimple = createPotentialMembersStructure(listsOfMembers);
    return { ...talentSearch.state.data, potential_members: listsSimple };
};

export const convertToTalentSearch = (ldSearchData: ISearchDataSimple, dataFromCurrentTalentSearch: any) => {
    const { data: ldSearch, name, id: ldSearchId } = ldSearchData.state;

    const getKey = ({ key, value }: { key: number; value: string }) => ({
        key,
        label: value,
    });

    const referenceId = ldSearch.referenceCompanyId;
    const peersIds = ldSearch.selectedPeers.map(getKey);
    const productIds = ldSearch.selectedProducts.map(getKey);
    const sectorIds = ldSearch.selectedSectors.map(getKey);
    const locationId = ldSearch.selectedCountry && { id: ldSearch.selectedCountry };

    return {
        data: {
            referenceId,
            peersIds,
            productIds,
            sectorIds,
            locationId,
            ldSearchId,
            ...dataFromCurrentTalentSearch,
        },
        name,
        source: "talent",
    };
};

export const createLdSearch = async (
    companyName: string,
    loggedInUserId: number,
    loggedInUserGroupId: number,
    searchData: ICurrentSearchData
): Promise<ICurrentSearchData> => {
    const loggedInUserSearches = await getSearchService(loggedInUserGroupId, loggedInUserId, {
        name: companyName,
        columns: ["name"],
    });

    // We omit talentSearchId if exist, in case we were saving a search for a different user
    const ldSearchData = omit({ ...searchData }, "talentSearchId");

    ldSearchData.name = autoNameSearch(
        companyName,
        Array.isArray(loggedInUserSearches.data.state)
            ? loggedInUserSearches.data.state
                  .filter((state) => state && state?.name)
                  .map((state) => state.name as string)
            : []
    );

    return ldSearchData;
};

export const forceDownload = (pdfResponse: AxiosResponse<Blob>, fileName?: string) => {
    const url = window.URL.createObjectURL(new Blob([pdfResponse.data]));
    const fileNameFromHeaders = pdfResponse.headers["x-file-name"];

    const link = document.createElement("a");
    link.href = url;

    link.setAttribute("download", fileNameFromHeaders || fileName || "Report");

    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(url);
};

export const getComputedStyles = (el: Element, propName: string) =>
    window.getComputedStyle(el).getPropertyValue(propName);

export const limitFreemiumParams = (columns: string[], isFreemium: boolean) => {
    const removeFromRequest = ["linkedin_id", "user_is_registered", "user_is_optin_share_data", "user_is_pace"];
    return isFreemium ? columns.filter((column) => !removeFromRequest.includes(column)) : columns;
};

export const isEmailValid = (email: string) => {
    return /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(email);
};

export const getMinimumDate = (subtractedYears: number) => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - subtractedYears);
    return date;
};

export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const randomNumber = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;

export const getElementBBox = (element: Highcharts.SVGElement) => {
    return {
        x: Number(element.attr("x") || element.attr("cx")),
        y: Number(element.attr("y") || element.attr("cy")),
        width: Number(element.attr("width")) || Number(element.attr("r")) || element.getBBox().width,
        height: Number(element.attr("height")) || Number(element.attr("r")) || element.getBBox().height,
        e: Number(element.attr("translateX")),
        f: Number(element.attr("translateY")),
    };
};

export const shouldForwardProp = <CustomProps extends Record<string, unknown>>(
    props: Array<keyof CustomProps>,
    prop: PropertyKey
): boolean => !props.includes(prop as string);

export const getNameInitials = (name: string) =>
    name
        .split(" ")
        .map((name: string) => name[0])
        .join("");

export const dateToString = (date: Date | null, formatStyle = "yyyy-MM-dd") => {
    return date !== null ? format(date, formatStyle) : date;
};

export const snakeCaseToCapitalizedString = (string: string) =>
    string
        .split("_")
        .map((narr) => capitalize(narr))
        .join(" ");

export const toChipArrayFormat = (obj: { id: number; name: string; sector_name?: string }) => ({
    key: obj.id,
    value: obj.name ? obj.name : obj.sector_name,
    selected: true,
});

export const buildParamsString = (params: Record<string, string | number | undefined | null | boolean>) => {
    return Object.entries(params || {})
        .filter(
            (param) => param[1] !== null && param[1] !== undefined && param[1] !== "undefined" && param[1] !== "NaN"
        )
        .map((param) => `${param[0]}=${param[1]}`)
        .join("&");
};

export const extractMemberIds = (members: Member[]) => members.map(({ person_id }) => person_id);

export const facetToNarrative = (data: FacetObjectNarrative) => ({
    id: data.type.type,
    label: data.type.readable_name,
    weight: data.weight,
});

export const mergeFacetWithMetaUi = (
    facetData: FacetObjectNarrative[],
    facetMetaUi?: SavedFacetObjectNarrative[],
    useFacetWeight = false
) =>
    sortBy(
        facetData.map((facet) => {
            const relatedMetaUi = facetMetaUi?.find((metaUi) => metaUi.type === facet.type.type);
            return {
                id: facet.type.type,
                label: facet.type.readable_name,
                weight: (useFacetWeight ? facet.weight : relatedMetaUi?.weight) || 0,
            };
        }),
        ["type.sort_value"]
    );

export const extractFacetData = (facetList: Facet[]) => {
    return facetList.map((facet) => ({
        type: facet.id,
        weight: facet.weight,
    }));
};

export const generateID = () => Date.now() + Math.random();

/**
 * Get UI User Role from a list of permissions
 *
 * @param {TSelectedPermissions} selectedPermission
 * @param {RoleBundleResponse} permissionsBundle
 * @param {TUserRole[]} allUserRoles
 * @returns {*}
 */
export const getUserRolesByPermissions = (
    selectedPermission: TSelectedPermissions,
    permissionsBundle: RoleBundleResponse,
    allUserRoles: TUserRole[]
) => {
    const currentPermissionNames = Object.values(selectedPermission).flatMap((p) => p?.name);
    const allRoles = permissionsBundle.bundles
        .filter((permission) => currentPermissionNames.includes(permission.name))
        .flatMap((permission) => permission.user_functions);

    const newRoles = allUserRoles.map((role) => {
        const userRoles = allRoles?.filter((r) => r.name === role.id);
        return {
            ...role,
            userRoles: userRoles.flatMap((role) => role.roles),
        };
    });

    return newRoles;
};

/**
 * Take an array of API User Roles like: ["PACE_SAAS_BASIC", "ORG_ADMIN_BASIC", "LD_SAAS_FREE"] and map it to
 * it's corresponding UI User Role: ADMIN, VIEW_EDIT, VIEW_ONLY, PROFILE.
 *
 * @see allUserRoles - constants/constants.ts for a list of UI User Roles
 * @param {{ id: number; name: string }[]} userRolesAPI - list of roles to map back to UI User Role
 * @param {TUserRole[]} userRolesUI - list of user roles
 * @returns {TUserRole | undefined}
 */
export const getUISelectedUserRole = (userRolesAPI: { id: number; name: string }[], userRolesUI?: TUserRole[]) => {
    if (userRolesUI === undefined) return;

    return userRolesUI.filter((role) => !differenceBy(userRolesAPI, role.userRoles as Role[], "id").length)[0];
};

/**
 * Get the corresponding permission name out of a list of roles. Check inside the Role Bundle and extract the role name based on
 * the list of API Roles
 *
 * @param {RoleBundleResponse} permissionsBundle
 * @param {SimpleEntity[]} clientHubRoles
 * @param {("LD" | "PACE")} type
 * @returns {RoleBundle}
 */
export const getPermissionNameFromRolesList = (
    permissionsBundle: RoleBundleResponse,
    clientHubRoles: SimpleEntity[],
    type: "LD" | "PACE"
) => {
    const allRolesType = permissionsBundle.bundles?.filter((p) => p.name.includes(type));
    const clientHubRoleNames = clientHubRoles.map((role) => role.name);

    return allRolesType.filter(
        (r) =>
            difference(
                clientHubRoleNames,
                r.roles.map((role) => role.name)
            ).length === 0
    )[0];
};

/**
 * Get a Brief data and check if all required data is there. A valid Brief should have a Vacancy, Role, both charts
 * Functional and Individual (x, y and radius) and all Facets' weight values other than 0
 *
 * @param {(BriefChildren | undefined)} briefData
 * @returns {boolean}
 */
export const isBriefCompleted = (briefData: BriefChildren | undefined) => {
    if (!briefData) return false;

    const {
        crm_vacancy,
        role_function,
        role_level,
        functional_balance,
        individual_positioning,
        facet_situational,
        facet_domain,
        facet_functional,
    } = briefData.data;

    const hasValidVacancy = Boolean(crm_vacancy);
    const hasValidRole = role_function !== "";
    const hasValidFunction = role_level !== "";

    const hasValidFBChart = Object.values(functional_balance).every((val) => val !== undefined);
    const hasValidIPChart = Object.values(individual_positioning).every((val) => val !== undefined);

    const areAllFacetsValid = [
        ...facet_situational.narratives,
        ...facet_domain.narratives,
        ...facet_functional.narratives,
    ].every((facet) => facet.weight);

    return Boolean(
        hasValidVacancy && hasValidRole && hasValidFunction && hasValidFBChart && hasValidIPChart && areAllFacetsValid
    );
};

/**
 * Return the same array from allUserRoles from constants.ts
 * with the possibility to change the label of the placeholder value
 *
 * @param {string} label
 * @returns {*}
 */
export const getUserRolesWithCustomPlaceholder = (label = "All (default)") =>
    allUserRoles.map((role) => {
        if (role.id === "placeholder") {
            return { ...role, label };
        }
        return role;
    });

export const mergeSeriesIntoLD12Series = (
    data: (TBCSectionData["series"] | undefined)[]
): Record<string, (number | null)[]> => {
    const result: Record<string, (number | null)[]> = {};
    // Iterate over the array Record<string, number | null>[]
    for (const obj of data) {
        // Make sure obj is not undefined
        if (obj) {
            // Iterate over the keys of: Record<string, number | null>
            for (const key in obj) {
                // Make sure key is in obj and not in it's prototype
                if (obj.hasOwnProperty(key)) {
                    // If key isn't in result yet, add it and put an empty array as it's value
                    // For example: {"0": []}
                    if (!result[key]) {
                        result[key] = [];
                    }
                    // Push the value to the corresponding key
                    // For example: {"0": [1]}
                    result[key].push(obj[key]);
                }
            }
        }
    }
    return result;
};

export const mergeLD12Series = (prevSerie: any, newSerie: any) =>
    mergeWith({}, prevSerie, newSerie, (prevValue, newValue) => {
        if (isArray(prevValue) && isArray(newValue)) {
            return [...prevValue, ...newValue];
        }
        // by returning undefined we let mergeWith deal with the merge
        return undefined;
    });

export const removeFromLD12Series = (data: LD12Chart, indexesToRemove: number[]) => {
    const { series_labels, series } = data;

    // Remove indexes from Serie Label
    const newSeriLabels = series_labels.filter((value, index) => {
        return indexesToRemove.indexOf(index) === -1;
    });

    // Convert series into array
    const seriesWithoutIndexesToRemove = Object.entries(series).map((serie) => {
        // Remove indexes from each serie
        const data = serie[1].filter((value, index) => {
            return indexesToRemove.indexOf(index) === -1;
        });
        return [serie[0], data];
    });

    // Convert series back to an object
    const newSeries = Object.fromEntries(seriesWithoutIndexesToRemove);

    return {
        ...data,
        series: newSeries,
        series_labels: newSeriLabels,
    };
};

export const mergeIndividualIntoLD12Series = (chartData: any, newData: TBCSectionData[]) => {
    let mergeSeries = chartData?.series || {};
    let seriesLabels = chartData?.series_labels || [];

    newData.forEach((newData) => {
        const { series, ...newDataSerieLabel } = newData;

        const serieToArray = Object.values(series);
        const newSerieToRecord = serieToArray.map((e) => [e]);

        mergeSeries = mergeLD12Series(mergeSeries, newSerieToRecord);
        seriesLabels = [...seriesLabels, newDataSerieLabel];
    });

    return {
        ...chartData,
        series: mergeSeries,
        series_labels: seriesLabels,
    };
};

export const getPillColorValue = (label: string) => {
    return functionsPillColor[label as keyof typeof functionsPillColor] || null;
};

export const getRetakeAssessmentDate = (retakeCountdown: IPaceCountdown | null | undefined, staleId: string) => {
    let retakeAvailableOn = "";

    if (retakeCountdown) {
        const months = retakeCountdown.months || 0;
        const days = retakeCountdown.days || 0;
        const hours = retakeCountdown.hours || 0;
        const minutes = retakeCountdown.minutes || 0;

        if (months <= 0 && days <= 0 && hours <= 0 && minutes <= 0) {
            retakeAvailableOn = "Your next retake is available now";
        } else if (months >= 1) {
            const unit = months === 1 ? "Month" : "Months";
            retakeAvailableOn = `in ${months} ${unit}`;
        } else if (days >= 1) {
            const unit = days === 1 ? "Day" : "Days";
            retakeAvailableOn = `in ${days} ${unit}`;
        } else if (hours >= 1 || minutes > 0) {
            const hoursDisplay = hours > 0 ? hours : 1;
            const unit = hoursDisplay === 1 ? "Hour" : "Hours";
            retakeAvailableOn = `in ${hoursDisplay} ${unit}`;
        }
    }

    let statusBarColor;
    if (staleId === "RECENT") {
        statusBarColor = theme.palette.success.main;
    } else if (staleId === "STALE_3") {
        statusBarColor = theme.palette.others.pacificBlue;
    } else if (staleId === "STALE_6") {
        statusBarColor = theme.palette.warning.main;
    } else if (staleId === "OLD") {
        statusBarColor = theme.palette.error.main;
    } else {
        statusBarColor = theme.palette.success.main;
    }

    return {
        retakeAvailableOn,
        statusBarColor,
    };
};

export const getStatusCardPaceInfo = (statusData: TPaceAssessmentReportStatus) => {
    return {
        statusLegend: statusData?.pace_stale_status?.message || "",
        paceStaleId: statusData?.pace_stale_status?.id || "",
        retakeCountdown: statusData?.pace_stale_status?.countdown,
        isRetakeAvailable:
            statusData?.pace_stale_status?.id === "STALE_3" ||
            statusData?.pace_stale_status?.id === "STALE_6" ||
            statusData?.pace_stale_status?.id === "OLD",
    };
};

/**
 * 
 * @param {string} linkedIn 
 * @returns {boolean}
 * Valid with for urls like:
    https://www.linkedin.com/in/XXXXXX-XXXXX-55301b41
    https://www.linkedin.com/pub/XXXXXX-XXXXX-55301b41
    https://www.linkedin.com/public-profile/in/XXXXXX-XXXX-b82a7b10a
    https://www.linkedin.com/public-profile/pub/XXXXXX-XXXX-b82a7b10a
    https://www.linkedin.com/in/xxxx-xxxx-ab85a328/%7Bcountry%3Dde%2C+language%3Dde%7D?trk=people-guest_profile-result-card_result-card_full-click
    https://www.linkedin.com/edu/school?id=18987
    https://www.linkedin.com/in/xxxx-xxxx-55301b41/de
    https://www.linkedin.com/pub/xxxxx-xxxxx/98/b7a/22b
    https://it.linkedin.com/public-profile/in/xxxx-xxxxx-80520667?challengeId=AQEV3tHveORYsAAAAXQqx1N3bZ7fd44s5ngegJp6rC0UoWvhG2LtAhhgld8h3QyBxorfcLL2iuvK4xh_UKoGguxvX6sFx_nnZA&submissionId=7bc972ed-bfd1-2e16-c395-ad35cb550117
 * Valid with IDs like:
    elissa-corrigan-baaa4837
    http
*/
export const isLinkedValid = (linkedIn: string) => {
    const validLength = linkedIn && linkedIn.trim().length >= 3 && linkedIn.trim().length <= 200;
    const regexUrlOrId =
        /^(https?:\/\/)?((www|\w{2})\.)?linkedin\.com\/(?!feed\/update\/urn)((public-profile\/(in|pub)\/|edu\/school\?id=|in\/|pub\/).+)|^(?!http:|https:|www)([a-z0-9\-_]{3,100})$/gim;

    if (linkedIn === "" || (validLength && regexUrlOrId.test(linkedIn))) {
        return true;
    }

    return false;
};

export const getGroupColor = (colorLabel: string | null) => {
    if (colorLabel === "GREEN") return theme.palette.others.dewGreen;
    if (colorLabel === "AMBER") return theme.palette.others.floralWhite;

    return theme.palette.others.mistyRose;
};

export const getAbilityIcon = (colorLabel: string | null) => {
    return AbilityIconColor[colorLabel as keyof typeof AbilityIconColor] ?? icons.exclamation;
};

export const getYearsList = (startYear: number, endYear = new Date().getFullYear(), order: Order = "asc"): number[] => {
    const handleIteration = (i: number) => {
        if (order === "asc") return startYear + i;
        return endYear - i;
    };

    const years = Array.from({ length: endYear - startYear + 1 }, (_, i) => handleIteration(i));

    return years;
};
